import type { BuildOperation } from '../types/globals';

const OPERATIONS_FILE_NAME = 'operations.json';

const { country, language } = CSS_CONFIG;

/**
 * Validated runtime operations that have been fetched from the CDN.
 * Falls back to build-time operations if the fetch fails or the file is
 * outdated (same logic as runtime translations).
 */
let runtimeOperations: BuildOperation[] | null = null;

/**
 * Cached promise for the in-flight or completed CDN fetch.
 * Allows callers to await the result without triggering multiple fetches.
 */
let loadPromise: Promise<void> | null = null;

const getOperationsUrl = (): string => {
  const host = TARGET === 'prod' ? 'www.ikea.com' : 'www.cte.ikeadt.com';
  return `https://${host}/${country}/${language}/appointment/assets/operations/${OPERATIONS_FILE_NAME}`;
};

const fetchOperationsFile = async (
  url: string,
): Promise<{
  operations: BuildOperation[];
  timestampBokaDeploy: number;
}> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to load operations from ${url}: ${response.status}`,
    );
  }

  const data = await response.json();
  if (!data.timestampBokaDeploy || data.timestampBokaDeploy < TIMESTAMP) {
    throw new Error(`Operations file from ${url} is outdated`);
  }
  if (!Array.isArray(data.operations)) {
    throw new Error(`Operations file from ${url} is invalid`);
  }
  return {
    operations: data.operations,
    timestampBokaDeploy: data.timestampBokaDeploy,
  };
};

export const loadRuntimeOperations = (): Promise<void> => {
  if (loadPromise) {
    return loadPromise;
  }

  if (TARGET !== 'stage' && TARGET !== 'prod') {
    return Promise.resolve();
  }

  // Do not load runtime operations for sub-path deployments (e.g. PR environments),
  // as operations.json is only uploaded for full market deployments.
  if (SUB_PATH !== '') {
    return Promise.resolve();
  }

  loadPromise = (async () => {
    try {
      const url = getOperationsUrl();
      const data = await fetchOperationsFile(url);
      runtimeOperations = data.operations;
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.warn(
          'Failed to load runtime operations, using build-time fallback:',
          error.message,
        );
      } else {
        console.warn(
          'Failed to load runtime operations, using build-time fallback:',
          error,
        );
      }
    }
  })();

  return loadPromise;
};

/**
 * Returns the current list of valid operations. Prefers runtime-fetched
 * operations (deployed independently) over the build-time list.
 */
export const getOperations = (): BuildOperation[] =>
  runtimeOperations ?? __BUILD_OPERATIONS__;

export const isBusinessOperation = (operation: string): boolean => {
  const op = getOperations().find((o) => o.name === operation);
  return op?.isBusiness ?? false;
};
